import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { buildApiUrl } from '../../../services/apiConfig';

const useSocketConnection = (setInput, setIsRecording) => {
  const socketRef = useRef(null);
  const transcriptionLogRef = useRef([]);
  const transcriptionTextRef = useRef("");
  const accumulatedTextRef = useRef(""); // Store accumulated text across sessions

  useEffect(() => {
    const socketUrl = buildApiUrl('SOCKET');
    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      autoConnect: false
    });

    const socket = socketRef.current;

    socket.resetAccumulatedText = () => {
      accumulatedTextRef.current = '';
    };

    socket.on("connect", () => {
      console.log("✅ WebSocket connected successfully");
    });

    socket.on("transcription", (data) => {
      if (!data || !data.text) {
        console.log("Invalid transcription data received");
        return;
      }

      // Log every transcription event with timestamp
      const timestamp = new Date().toISOString();
      const transcriptionEvent = {
        timestamp,
        text: data.text,
        type: data.text.startsWith(">>") ? "interim" : "final"
      };
      transcriptionLogRef.current.push(transcriptionEvent);

      // Handle command transcriptions (starting with ##)
      if (data.text.startsWith("##")) {
        const command = data.text.replace("##", "").trim();

        // Accumulate command text instead of replacing
        if (accumulatedTextRef.current === "") {
          accumulatedTextRef.current = command;
        } else {
          accumulatedTextRef.current += " " + command;
        }

        // Update the input with accumulated text
        setInput(accumulatedTextRef.current);
        transcriptionTextRef.current = accumulatedTextRef.current;
      } else if (data.text.startsWith(">>")) {
        // For interim results, don't accumulate, just show temporarily
        const interimText = data.text.replace(">>", "").trim();

        // Show accumulated text plus the interim text
        setInput(accumulatedTextRef.current + (accumulatedTextRef.current ? " " : "") + interimText);
      } else {
        // For final results, accumulate the text
        const finalText = data.text.trim();

        // Add to accumulated text with proper spacing
        if (accumulatedTextRef.current === "") {
          accumulatedTextRef.current = finalText;
        } else {
          accumulatedTextRef.current += " " + finalText;
        }

        // Update the input with the full accumulated text
        setInput(accumulatedTextRef.current);
        transcriptionTextRef.current = accumulatedTextRef.current;
      }
    });

    socket.on("connect_error", (error) => {
      console.warn("⚠️ Socket connection error (this is expected if not recording):", error.message);
    });

    socket.on("error", (error) => {
      console.warn("⚠️ Socket error:", error.message);
    });

    socket.on("disconnect", (reason) => {
      console.log("🔌 WebSocket disconnected:", reason);
      setIsRecording(false);
    });

    // Don't connect immediately, only connect when recording starts
    // socket.connect() will be called when needed

    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [setInput, setIsRecording]);

  return {
    socketRef,
    transcriptionLogRef,
    transcriptionTextRef
  };
};

export default useSocketConnection;